import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/services/auth/auth.service';
import { tenantToIDP, routeToTenantId } from '@app/config/auth-constants';

@Component({
  selector: 'app-sign-in-sso',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sign-in-sso.component.html',
  styleUrls: ['./sign-in-sso.component.css']
})
export class SignInSsoComponent implements OnInit, OnDestroy {
  tenant: string | null = null;
  isLoading = false;
  loadingMessage = '';
  private pageshowHandler = (event: PageTransitionEvent) => {
    // Reset loading state when page is restored from bfcache (back/forward navigation)
    if (event.persisted) {
      this.isLoading = false;
      this.loadingMessage = '';
    }
  };
  currentYear = new Date().getFullYear();

  // Logo URLs for different institutions
  private logoUrls: Record<string, string> = {
    'Brandeis': '/assets/images/logos/brandeis-logo.png',
    'Brandeis-Mock': '/assets/images/logos/brandeis-logo.png',
    'CSU': '/assets/images/logos/csu-logo.png',
    'CSU-Mock': '/assets/images/logos/csu-logo.png',
    'AmericanUniversity': '/assets/images/logos/au-logo.png',
    'AmericanUniversity-Mock': '/assets/images/logos/au-logo.png'
  };

  // Logo background colors for different institutions
  private logoBackgroundColors: Record<string, string> = {
    'Brandeis': '#243a74',
    'Brandeis-Mock': '#243a74',
    'CSU': 'transparent',
    'CSU-Mock': 'transparent',
    'AmericanUniversity': 'white',
    'AmericanUniversity-Mock': 'white'
  };

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Listen for pageshow event to handle bfcache restoration (browser back button)
    window.addEventListener('pageshow', this.pageshowHandler);

    // Get tenant from route path (e.g., /brandeis, /csu)
    this.route.url.subscribe(urlSegments => {
      if (urlSegments.length > 0) {
        const routeName = urlSegments[0].path;
        this.tenant = routeToTenantId[routeName] || null;
      }
    });

    // Check if user is already authenticated
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        const redirectUrl = localStorage.getItem('redirectUrl') || '/';
        localStorage.removeItem('redirectUrl');
        this.router.navigateByUrl(redirectUrl);
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('pageshow', this.pageshowHandler);
  }

  async handleContinueWithSSO(): Promise<void> {
    if (!this.tenant) {
      return;
    }

    this.isLoading = true;
    this.loadingMessage = 'Signing in, please wait…';
    try {
      await this.authService.signInWithSSO(this.tenant);
    } catch (error) {
      console.error('Error signing in with SSO:', error);
      this.isLoading = false;
      this.loadingMessage = 'Sign in failed. Please try again.';
    }
  }

  openPrivacyPolicy(): void {
    // Open privacy policy in a new tab or modal
    window.open('/privacy-policy', '_blank');
  }

  get idpName(): string {
    if (!this.tenant) return 'SSO';
    return tenantToIDP[this.tenant] || 'SSO';
  }

  get logoUrl(): string {
    const idp = this.idpName;
    return this.logoUrls[idp] || '/assets/images/logos/default-logo.svg';
  }

  get logoBackgroundColor(): string {
    const idp = this.idpName;
    return this.logoBackgroundColors[idp] || 'transparent';
  }

  get showEmailSignIn(): boolean {
    const idp = this.idpName;
    // Show email sign-in for CSU and American University, hide for Brandeis
    return idp.startsWith('CSU') || idp.startsWith('AmericanUniversity');
  }
}
